import { useMemo, useState } from 'react';
import type { MtoMaterialPosition, NsiMapping } from '@entities/mto';
import { needsCheck, positionKind, positionRaw, runRaw } from '@entities/mto';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Input } from '@shared/ui/Input';
import { IconSearch } from '@shared/ui/icons';
import { DocumentsCard } from './components/DocumentsCard';
import { MaterialsTable } from './components/MaterialsTable';
import { NotesCard } from './components/NotesCard';
import { RebarTable } from './components/RebarTable';
import { VedomostTiles } from './components/VedomostTiles';
import { matches } from './helpers';
import {
  Alert,
  CheckLabel,
  Counter,
  Filters,
  More,
  Muted,
  SearchWrap,
  SectionBar,
  SectionButton,
} from './styles';
import type { MtoVedomostViewProps, Tab } from './types';
import { PAGE, TAB_LABEL } from './types';

/**
 * Результат ведомости материалов по РД (модуль 1.2): сводка с оговорками,
 * документы, перечень материалов, свод арматуры, строки таблиц из приложенных
 * файлов (Excel, сканы), пояснения.
 */
export function MtoVedomostView({
  run,
  materials,
  mappings,
  canWrite,
}: MtoVedomostViewProps) {
  const raw = runRaw(run);
  const [tab, setTab] = useState<Tab>('materials');
  const [search, setSearch] = useState('');
  const [onlyCheck, setOnlyCheck] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [docsOpen, setDocsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(true);
  const [rowError, setRowError] = useState('');

  const nsiByPosition = useMemo(() => {
    const m = new Map<string, NsiMapping>();
    for (const x of mappings) if (x.material_position_id) m.set(x.material_position_id, x);
    return m;
  }, [mappings]);

  const byKind = useMemo(() => {
    const out: Record<Tab, MtoMaterialPosition[]> = {
      materials: [],
      rebar: [],
      tables: [],
      works: [],
    };
    for (const p of materials) {
      const k = positionKind(p);
      if (positionRaw(p).vor_work) out.works.push(p);
      else out[k === 'material' ? 'materials' : k === 'rebar' ? 'rebar' : 'tables'].push(p);
    }
    return out;
  }, [materials]);

  const tabs = (Object.keys(TAB_LABEL) as Tab[]).filter((t) => byKind[t].length > 0);
  const activeTab: Tab = byKind[tab].length > 0 ? tab : (tabs[0] ?? 'materials');
  const reviewIn = (t: Tab) => byKind[t].filter(needsCheck).length;

  const rows = useMemo(
    () =>
      byKind[activeTab].filter(
        (p) => matches(p, search.trim()) && (!onlyCheck || needsCheck(p)),
      ),
    [byKind, activeTab, search, onlyCheck],
  );
  // Разбивка по системам (разделы ОВ, ВК, НВ, НК) — графа перечня, если есть.
  const withSystems = useMemo(
    () => byKind.materials.some((p) => (positionRaw(p).by_system?.length ?? 0) > 0),
    [byKind],
  );

  const totals = raw.totals ?? {};
  const review = materials.filter(needsCheck).length;
  const rebarTons = totals.rebar_total_kg != null ? totals.rebar_total_kg / 1000 : null;
  const documents = raw.documents ?? [];
  const notIncluded = documents.filter((d) => d.status !== 'ok');
  // Ведомости до кратности по РД считали строки «на 1» только по пометке МТО.
  const perUnit = totals.per_unit_rows ?? materials.filter((p) => positionRaw(p).per_unit).length;
  const perUnitFound = totals.per_unit_found ?? 0;
  const perUnitManual = materials.filter((p) => positionRaw(p).multiplicity_manual != null).length;
  const works = byKind.works.length;
  const notes = raw.notes ?? [];

  function switchTab(t: Tab) {
    setTab(t);
    setShown(PAGE);
  }

  /** Плитка «проверить»: включает фильтр и открывает раздел, где такие строки. */
  function showReview() {
    const next = !onlyCheck;
    setOnlyCheck(next);
    setShown(PAGE);
    if (next && reviewIn(activeTab) === 0) {
      const t = tabs.find((x) => reviewIn(x) > 0);
      if (t) setTab(t);
    }
  }

  return (
    <>
      <VedomostTiles
        materialsCount={byKind.materials.length + byKind.tables.length}
        reviewCount={review}
        rebarTons={rebarTons}
        rebarMultTons={totals.rebar_total_mult_kg != null ? totals.rebar_total_mult_kg / 1000 : null}
        totalWeightTons={totals.total_weight_kg != null ? totals.total_weight_kg / 1000 : null}
        validDocsCount={documents.filter((d) => d.status === 'ok').length}
        projectName={raw.project_name}
        onlyCheck={onlyCheck}
        onShowReview={showReview}
        notIncludedCount={notIncluded.length}
        perUnit={perUnit}
        perUnitFound={perUnitFound}
        perUnitManual={perUnitManual}
        worksCount={works}
      />

      {documents.length > 0 && (
        <DocumentsCard
          documents={documents}
          docsOpen={docsOpen}
          onToggleDocsOpen={() => setDocsOpen((v) => !v)}
        />
      )}

      <Card>
        {tabs.length > 1 && (
          <SectionBar role="group" aria-label="Разделы ведомости">
            {tabs.map((t) => (
              <SectionButton
                key={t}
                type="button"
                $active={activeTab === t}
                aria-pressed={activeTab === t}
                onClick={() => switchTab(t)}
              >
                {TAB_LABEL[t]} · {byKind[t].length}
                {reviewIn(t) > 0 ? ` (проверить ${reviewIn(t)})` : ''}
              </SectionButton>
            ))}
          </SectionBar>
        )}

        <Filters>
          <SearchWrap>
            <IconSearch size={15} />
            <Input
              type="search"
              aria-label="Поиск по ведомости"
              placeholder="Поиск: бетон, A500С, свая…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShown(PAGE);
              }}
            />
          </SearchWrap>
          <CheckLabel>
            <input
              type="checkbox"
              checked={onlyCheck}
              onChange={(e) => {
                setOnlyCheck(e.target.checked);
                setShown(PAGE);
              }}
            />
            Только «проверить»
          </CheckLabel>
          <Counter role="status">
            показано {Math.min(shown, rows.length)} из {rows.length}
          </Counter>
        </Filters>

        {rowError && <Alert role="alert">{rowError}</Alert>}

        {rows.length === 0 ? (
          <Muted>
            {materials.length === 0
              ? 'Ведомость пуста — см. замечания.'
              : onlyCheck
                ? 'В этом разделе строк «проверить» нет.'
                : 'Под фильтр ничего не попало.'}
          </Muted>
        ) : activeTab === 'rebar' ? (
          <RebarTable
            rows={rows}
            shown={shown}
            runId={run.id}
            canWrite={canWrite}
            onRowError={setRowError}
          />
        ) : (
          <MaterialsTable
            rows={rows}
            shown={shown}
            activeTab={activeTab}
            withSystems={withSystems}
            nsiByPosition={nsiByPosition}
            runId={run.id}
            canWrite={canWrite}
            onRowError={setRowError}
          />
        )}
        {rows.length > shown && (
          <More>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={() => setShown((n) => n + PAGE)}
            >
              Показать ещё {Math.min(PAGE, rows.length - shown)}
            </Button>
          </More>
        )}
      </Card>

      {notes.length > 0 && (
        <NotesCard
          notes={notes}
          notesOpen={notesOpen}
          onToggleNotesOpen={() => setNotesOpen((v) => !v)}
        />
      )}
    </>
  );
}

export default MtoVedomostView;
